import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import { postFlightUpdatedLabel } from "@/features/post-flight/lib/post-flight-refresh";
import { formatExactDateTime } from "@/features/prompts/lib/prompt-list";

/** "Updated 5 minutes ago" moves on without a re-fetch. */
const LABEL_TICK_MS = 60_000;

const CONTROL_HEIGHT_CLASS =
  "h-11 min-h-11 text-[13px] [@media(hover:hover)_and_(pointer:fine)]:h-[34px] [@media(hover:hover)_and_(pointer:fine)]:min-h-0";

interface PostFlightRefreshControlProps {
  fetchedAt: string | undefined;
  isRefreshing: boolean;
  onRefresh: () => void;
}

export function PostFlightRefreshControl({ fetchedAt, isRefreshing, onRefresh }: PostFlightRefreshControlProps) {
  const { t, i18n } = useTranslation();
  const now = useMinuteClock(fetchedAt);
  const announcement = useRefreshAnnouncement(isRefreshing, fetchedAt);
  const isFetchedAtValid = fetchedAt !== undefined && Number.isFinite(Date.parse(fetchedAt));

  return (
    <div className="flex items-center gap-2">
      {/* Always rendered: a live region is only announced if it exists before its text changes. */}
      <span role="status" className="sr-only">
        {announcement ? t("postFlight.refresh.announced", "Post-Flight list updated") : ""}
      </span>
      {fetchedAt && isFetchedAtValid ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="text-[12px] text-muted-foreground">
                {postFlightUpdatedLabel(t, fetchedAt, i18n.language, now)}
              </span>
            </TooltipTrigger>
            <TooltipContent>{formatExactDateTime(fetchedAt, i18n.language)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={CONTROL_HEIGHT_CLASS}
        disabled={isRefreshing}
        onClick={onRefresh}
      >
        <Icon name="refresh-cw" color="inherit" className={isRefreshing ? "animate-spin" : undefined} />
        {isRefreshing
          ? t("postFlight.refresh.refreshing", "Updating…")
          : t("postFlight.refresh.button", "Refresh")}
      </Button>
    </div>
  );
}

/** Restarts on a new `fetchedAt`, so a just-refreshed list is not measured against a stale clock. */
function useMinuteClock(fetchedAt: string | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), LABEL_TICK_MS);
    return () => clearInterval(id);
  }, [fetchedAt]);
  return now;
}

/**
 * True after a refresh the user started has brought in a newer list. Not true on page load,
 * on the label's minute tick, on a failed refresh (its toast covers that), or for a list that
 * changed without a refresh. Cleared when the next refresh starts so the next success is
 * spoken again; the region only announces when its text changes.
 */
function useRefreshAnnouncement(isRefreshing: boolean, fetchedAt: string | undefined): boolean {
  const [isAnnouncing, setIsAnnouncing] = useState(false);
  const wasRefreshing = useRef(isRefreshing);
  const fetchedAtAtStart = useRef(fetchedAt);

  useEffect(() => {
    const hasStarted = isRefreshing && !wasRefreshing.current;
    const hasFinished = !isRefreshing && wasRefreshing.current;
    wasRefreshing.current = isRefreshing;
    if (hasStarted) {
      fetchedAtAtStart.current = fetchedAt;
      setIsAnnouncing(false);
    } else if (hasFinished && fetchedAt !== fetchedAtAtStart.current) {
      setIsAnnouncing(true);
    }
  }, [isRefreshing, fetchedAt]);

  return isAnnouncing;
}
